import React from 'react';
import { Link } from 'react-router-dom';

export const GRADE_OPTIONS = [
  { letter: 'A', points: 4 },
  { letter: 'B', points: 3 },
  { letter: 'C', points: 2 },
  { letter: 'D', points: 1 },
  { letter: 'F', points: 0 }
];

const GRADE_COLORS = {
  A: '#27ae60',
  B: '#3498db',
  C: '#f39c12',
  D: '#e67e22',
  F: '#e74c3c'
};

const pointsToLetter = (points) => {
  const match = GRADE_OPTIONS.find((option) => option.points === Number(points));
  return match ? match.letter : '';
};

const getAnswerId = (item) => item?.questionId?._id || item?.questionId || item?._id || '';

export default function GradeButton({ active = false, to = '/dashboard?tab=questions&focus=grade' }) {
  return (
    <Link to={to} className={`tab-btn${active ? ' active' : ''}`}>
      Grade
    </Link>
  );
}

export function GradePanel({
  answers,
  divisor,
  divisorOptions,
  onDivisorChange,
  onGradeChange,
  savingGradeId,
  gradeFeedback,
  selectedTitle
}) {
  const totalPoints = answers.reduce((sum, item) => sum + (Number(item.grade) || 0), 0);
  const gradedCount = answers.filter((item) => item.grade != null).length;
  const normalizedScore = divisor > 0 ? (totalPoints / divisor).toFixed(2) : '0.00';

  return (
    <section className="question-card" id="grade-panel">
      <div className="question-meta">
        <span className="question-number">Grade</span>
        <h2 className="question-title">All Saved Answers</h2>
      </div>

      <p className="question-text" style={{ marginBottom: '1.25rem' }}>
        {selectedTitle
          ? `Review every saved answer for ${selectedTitle}, assign a letter grade (A–F), and watch the total score update.`
          : 'Review every saved answer, assign a letter grade (A–F), and watch the total score update.'}
      </p>

      {/* Score summary card */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: '0.75rem',
          padding: '1rem',
          background: 'var(--color-primary-light)',
          border: '1px solid var(--color-primary)',
          borderRadius: '10px',
          marginBottom: '1rem'
        }}
      >
        <SummaryStat label="Total points" value={totalPoints} />
        <SummaryStat label="Graded" value={`${gradedCount} / ${answers.length}`} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          <span style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--color-text-light)' }}>
            Divide by
          </span>
          <select
            id="grade-divisor"
            value={divisor}
            onChange={(event) => onDivisorChange(Number(event.target.value))}
            className="input"
            style={{ padding: '0.45rem 0.6rem', fontWeight: 700 }}
          >
            {divisorOptions.map((value) => (
              <option key={value} value={value}>
                /{value}
              </option>
            ))}
          </select>
        </div>
        <SummaryStat label="Score" value={normalizedScore} highlight />
      </div>

      {gradeFeedback && <div className="answer-help" style={{ marginBottom: '1rem' }}>{gradeFeedback}</div>}

      {answers.length === 0 ? (
        <div className="answer-help">Save an answer first. Your saved answers will show up here for grading.</div>
      ) : (
        <div style={{ display: 'grid', gap: '1rem' }}>
          {answers.map((item, index) => {
            const questionData = item.questionId && typeof item.questionId === 'object' ? item.questionId : null;
            const currentLetter = pointsToLetter(item.grade);
            const isSaving = savingGradeId && savingGradeId === getAnswerId(item);
            const rowKey = item._id || `${questionData?._id || 'answer'}-${index}`;

            return (
              <article
                key={rowKey}
                className="question-example"
                style={{
                  padding: '1rem',
                  border: '1px solid var(--color-border)',
                  borderRadius: '10px',
                  background: 'var(--color-white)'
                }}
              >
                {/* Header row */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '0.75rem',
                    flexWrap: 'wrap',
                    marginBottom: '0.75rem'
                  }}
                >
                  <span
                    style={{
                      display: 'inline-block',
                      padding: '0.25rem 0.6rem',
                      background: 'var(--color-dark)',
                      color: 'var(--color-white)',
                      borderRadius: '999px',
                      fontSize: '0.8rem',
                      fontWeight: 700,
                      letterSpacing: '0.04em'
                    }}
                  >
                    {questionData?.questionNumber ? `Q${questionData.questionNumber}` : `Answer ${index + 1}`}
                  </span>
                  <span
                    style={{
                      padding: '0.25rem 0.6rem',
                      background: 'var(--color-primary-light)',
                      color: 'var(--color-dark)',
                      borderRadius: '999px',
                      fontSize: '0.8rem',
                      fontWeight: 600
                    }}
                  >
                    {questionData?.businessTitle || item.businessTitle || 'Business Title'}
                  </span>
                </div>

                {/* Question */}
                <p style={{ margin: '0 0 0.75rem', color: 'var(--color-dark)', fontWeight: 600 }}>
                  {questionData?.questionText || 'Question unavailable.'}
                </p>

                {/* Answer callout */}
                <div
                  style={{
                    padding: '0.85rem 1rem',
                    background: 'var(--color-snow)',
                    borderLeft: '4px solid var(--color-primary)',
                    borderRadius: '6px',
                    marginBottom: '1rem',
                    whiteSpace: 'pre-wrap',
                    color: 'var(--color-dark)',
                    fontWeight: 800,
                    fontSize: '1rem',
                    lineHeight: 1.5
                  }}
                >
                  {item.answerText?.trim() || 'No saved answer text yet.'}
                </div>

                {/* Grade pills */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '0.5rem'
                  }}
                >
                  <span style={{ fontWeight: 700, marginRight: '0.25rem', color: 'var(--color-text-light)' }}>
                    Grade:
                  </span>
                  {GRADE_OPTIONS.map((option) => {
                    const isActive = currentLetter === option.letter;
                    const color = GRADE_COLORS[option.letter];
                    return (
                      <button
                        key={option.letter}
                        type="button"
                        onClick={() => onGradeChange(item, option.letter)}
                        disabled={isSaving}
                        aria-pressed={isActive}
                        title={`${option.letter} = ${option.points} pts`}
                        style={{
                          minWidth: '44px',
                          padding: '0.45rem 0.7rem',
                          borderRadius: '8px',
                          border: `2px solid ${color}`,
                          background: isActive ? color : 'var(--color-white)',
                          color: isActive ? 'var(--color-white)' : color,
                          fontWeight: 800,
                          fontSize: '1rem',
                          cursor: isSaving ? 'wait' : 'pointer',
                          transition: 'background 0.15s ease, color 0.15s ease',
                          opacity: isSaving ? 0.6 : 1
                        }}
                      >
                        {option.letter}
                      </button>
                    );
                  })}
                  <span style={{ marginLeft: '0.5rem', color: 'var(--color-text-light)', fontSize: '0.9rem' }}>
                    {currentLetter
                      ? <>Worth <strong>{Number(item.grade) || 0}</strong> pts</>
                      : 'Not graded yet'}
                  </span>
                  {isSaving && (
                    <span style={{ marginLeft: '0.5rem', color: 'var(--color-info)', fontSize: '0.85rem' }}>
                      Saving…
                    </span>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}

function SummaryStat({ label, value, highlight = false }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
      <span
        style={{
          fontSize: '0.8rem',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: 'var(--color-text-light)'
        }}
      >
        {label}
      </span>
      <strong
        style={{
          fontSize: highlight ? '1.4rem' : '1.15rem',
          color: highlight ? 'var(--color-secondary-dark)' : 'var(--color-dark)',
          fontWeight: 800
        }}
      >
        {value}
      </strong>
    </div>
  );
}

